import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { format, parseISO, startOfDay, startOfHour, eachDayOfInterval, eachHourOfInterval } from "date-fns";

interface EmailLogEntry {
  id: string;
  status: string;
  created_at: string;
}

type TimeRange = "24h" | "7d" | "30d";

interface EmailTrendChartProps {
  logs: EmailLogEntry[];
  timeRange: TimeRange;
  startDate: Date;
}

const EmailTrendChart = ({ logs, timeRange, startDate }: EmailTrendChartProps) => {
  const chartData = useMemo(() => {
    const now = new Date();
    const isHourly = timeRange === "24h";
    const bucketFn = isHourly ? startOfHour : startOfDay;
    const intervals = isHourly
      ? eachHourOfInterval({ start: startDate, end: now })
      : eachDayOfInterval({ start: startDate, end: now });

    const buckets = new Map<string, { sent: number; failed: number; suppressed: number }>();
    intervals.forEach((d) => {
      buckets.set(bucketFn(d).toISOString(), { sent: 0, failed: 0, suppressed: 0 });
    });

    logs.forEach((l) => {
      const key = bucketFn(parseISO(l.created_at)).toISOString();
      const bucket = buckets.get(key);
      if (!bucket) return;
      if (l.status === "sent") bucket.sent++;
      else if (l.status === "dlq" || l.status === "failed") bucket.failed++;
      else if (l.status === "suppressed") bucket.suppressed++;
    });

    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, val]) => ({
        date: key,
        label: isHourly
          ? format(parseISO(key), "h a")
          : format(parseISO(key), "MMM d"),
        ...val,
      }));
  }, [logs, timeRange, startDate]);

  if (chartData.length === 0) return null;

  return (
    <Card className="border-border">
      <CardHeader className="pb-2">
        <CardTitle className="font-display text-lg">Delivery Trends</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[250px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="failedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="suppressedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                className="text-muted-foreground"
                interval={timeRange === "24h" ? 3 : timeRange === "30d" ? 4 : 0}
              />
              <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
              <Area type="monotone" dataKey="sent" name="Sent" stroke="hsl(var(--primary))" fill="url(#sentGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="failed" name="Failed" stroke="hsl(var(--destructive))" fill="url(#failedGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="suppressed" name="Suppressed" stroke="#f59e0b" fill="url(#suppressedGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailTrendChart;
